import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Add, Delete } from '@mui/icons-material';
import { 
  Autocomplete,
  Box, 
  FormControl,
  FormHelperText,
  InputLabel, 
  MenuItem, 
  Select, 
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import RegularButton from '../../components/CustomButtons/Button';
import { GROUP_TYPES } from './config';
import {
  createNewCoverageGroup,
  editCoverageGroup,
  fetchCoveragesByCoverageGroup,
  fetchCoverageGroupById
} from '../../redux/actions/coverageGroups/coverageGroupsActions';
import { useForm } from '../../hooks/useForm';
import { useSearch } from '../../hooks/useSearch';

const blue = '#0055aa'

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginTop: theme.spacing(3),
    minWidth: 120,
  },
  selectEmpty: {
  },
  coveragesList: {
    marginTop: theme.spacing(2),
    maxHeight: '300px',
    overflow: 'auto',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: theme.spacing(1),
  },
  coverageItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1),
    borderBottom: '1px solid #f0f0f0',
  },
  addCoverageBox: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    alignItems: 'center',
  }
}));

export const FormCoverageGroupEdit = (props) => {
  const { handleClose, add, id_station, station_name } = props
  const classes = useStyles()
  const { coverageGroupSelected, availableCoverages } = useSelector(state => state.coverageGroups)
  const { inputValues, handleInputOnChange, handleNewValues, runValidates, fieldErrors } = useForm({
    name: '',
    type: '',
    polygon_ids: []
  }, {
    name: { required: true },
    type: { required: true }
  })
  const { name, type, polygon_ids } = inputValues
  const { setSnackbar } = useSearch();
  const [selectedCoverage, setSelectedCoverage] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [previousType, setPreviousType] = useState('');
  const [pendingType, setPendingType] = useState(null);
  const [showTypeChangeConfirm, setShowTypeChangeConfirm] = useState(false);

  const dispatch = useDispatch()

  useEffect(() => {
    if (!add && coverageGroupSelected && coverageGroupSelected.id) {
      setInitialLoadComplete(false);
      
      dispatch(fetchCoverageGroupById(coverageGroupSelected.id))
        .then((response) => {
          if (response) {
            handleNewValues({
              name: response.name || '',
              type: response.type || '',
              polygon_ids: []
            });

            if (response.polygons && response.polygons.length > 0) {
              const polygonIds = response.polygons.map(p => p.id_route_polygon);
              
              handleNewValues({
                name: response.name || '',
                type: response.type || '',
                polygon_ids: polygonIds
              });
            }

            if (response.type && response.id_station) {
              dispatch(fetchCoveragesByCoverageGroup(response.id_station, response.type));
            }
            
            // Guardar el tipo inicial para detectar cambios
            setPreviousType(response.type || '');
            
            setTimeout(() => setInitialLoadComplete(true), 100);
          }
        });
    } else if (add) {
      setInitialLoadComplete(true);
    }
  }, [add, coverageGroupSelected?.id, dispatch]);

  useEffect(() => {
    if (id_station && type && initialLoadComplete) {
      dispatch(fetchCoveragesByCoverageGroup(id_station, type));
    }
  }, [id_station, type, dispatch, initialLoadComplete]);

  useEffect(() => {
    if (type && initialLoadComplete) {
      handleNewValues({
        ...inputValues,
        polygon_ids: []
      });
      setSelectedCoverage(null);
    }
  }, [type]);

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    
    // Si es modo edición, hay coberturas seleccionadas y el tipo es diferente al anterior
    if (!add && polygon_ids.length > 0 && initialLoadComplete && newType !== previousType) {
      setPendingType(newType);
      setShowTypeChangeConfirm(true);
    } else {
      // En modo agregar o sin coberturas, cambiar directamente
      handleInputOnChange(event);
      setPreviousType(newType);
    }
  };

  const handleConfirmTypeChange = () => {
    // Confirmar el cambio de tipo
    handleInputOnChange({ target: { name: 'type', value: pendingType } });
    setPreviousType(pendingType);
    setShowTypeChangeConfirm(false);
    setPendingType(null);
  };

  const handleCancelTypeChange = () => {
    // Cancelar el cambio de tipo
    setShowTypeChangeConfirm(false);
    setPendingType(null);
  };

  const handleSubmit = () => {
    if (runValidates()) return

    if (!id_station) {
      return setSnackbar('Debe seleccionar una planta primero')
    }

    if (polygon_ids.length === 0) {
      return setSnackbar('Debe seleccionar al menos una cobertura')
    }

    const form = {
      name,
      type,
      id_station,
      polygon_ids
    }

    if (add) {
      dispatch(createNewCoverageGroup(form, handleClose))
    } else {
      dispatch(editCoverageGroup(coverageGroupSelected.id, form, handleClose))
    }
  }

  const handleAddCoverage = () => {
    if (!selectedCoverage) {
      return setSnackbar('Debe seleccionar una cobertura')
    }

    if (polygon_ids.includes(selectedCoverage.id)) {
      return setSnackbar('Esta cobertura ya ha sido agregada')
    }

    handleNewValues({
      ...inputValues,
      polygon_ids: [...polygon_ids, selectedCoverage.id]
    })
    setSelectedCoverage(null);
  }

  const handleRemoveCoverage = (coverageId) => {
    handleNewValues({
      ...inputValues,
      polygon_ids: polygon_ids.filter(id => id !== coverageId)
    })
  }

  const getCoverageName = (coverageId) => {
    const coverage = availableCoverages.find(c => c.id === coverageId);
    return coverage ? coverage.name : coverageId;
  }

  return (
    <>
      <FormControl fullWidth onSubmit={handleSubmit}>
        <TextField
          type="text"
          className="input_custom"
          label="Planta"
          name="station"
          value={station_name || ''}
          disabled
          variant="outlined" 
          style={{ marginTop: '20px' }}
        />
        
        <TextField
          type="text"
          className="input_custom"
          label="Nombre *"
          name="name"
          value={name}
          onChange={handleInputOnChange}
          error={!!fieldErrors.name}
          helperText={fieldErrors.name || ""}
          variant="outlined" 
          style={{ marginTop: '20px' }}
        />

        <FormControl variant="outlined" className={classes.formControl} error={!!fieldErrors.type}>
          <InputLabel htmlFor="outlined-type">Tipo *</InputLabel>
          <Select
            value={type}
            name='type'
            onChange={handleTypeChange}
            label="Tipo"
          >
            <MenuItem value=''>Seleccione un tipo</MenuItem>
            {
              GROUP_TYPES.map(({ label, value }) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))
            }
          </Select>
          <FormHelperText style={{ color: 'red' }}>{fieldErrors.type || ""}</FormHelperText>
        </FormControl>

        <Box className={classes.addCoverageBox}>
          <Autocomplete
            options={availableCoverages || []}
            getOptionLabel={(option) => option.name || ''}
            value={selectedCoverage}
            onChange={(event, newValue) => {
              setSelectedCoverage(newValue);
            }}
            disabled={!type}
            noOptionsText="No hay coberturas disponibles"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Seleccionar Cobertura"
                variant="outlined"
                placeholder="Buscar cobertura..."
              />
            )}
            style={{ flexGrow: 1 }}
          />
          <RegularButton
            color="info"
            justIcon
            round
            onClick={handleAddCoverage}
            disabled={!type}
          >
            <Add />
          </RegularButton>
        </Box>

        {polygon_ids.length > 0 && (
          <Box className={classes.coveragesList}>
            <List>
              {polygon_ids.map((coverageId) => (
                <ListItem key={coverageId} className={classes.coverageItem}>
                  <ListItemText primary={getCoverageName(coverageId)} />
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveCoverage(coverageId)}
                    size="small"
                  >
                    <Delete style={{ color: blue }} />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', gap: '10px' }}>
          <RegularButton
            color="transparent"
            onClick={handleClose}
          >
            Cancelar
          </RegularButton>
          <RegularButton
            color="info"
            onClick={handleSubmit}
          >
            {add ? 'Agregar' : 'Guardar'}
          </RegularButton>
        </Box>
      </FormControl>

      {/* Dialog de confirmación de cambio de tipo */}
      <Dialog
        open={showTypeChangeConfirm}
        onClose={handleCancelTypeChange}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirmar cambio de tipo"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Al cambiar el tipo de agrupación, todas las coberturas actualmente seleccionadas serán eliminadas.
            Esta acción no se puede deshacer. ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <RegularButton onClick={handleCancelTypeChange} color="transparent">
            Cancelar
          </RegularButton>
          <RegularButton onClick={handleConfirmTypeChange} color="danger" autoFocus>
            Confirmar
          </RegularButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
